import { useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Bot, Loader2, Sparkles, Lock, AlertTriangle, Layers, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { AIMediaCapture } from './AIMediaCapture';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_API_URL;

const INDUSTRY_KEYS = [
  'electrical', 'plumbing', 'hvac', 'painting', 'landscaping',
  'cleaning', 'renovation', 'repair', 'security_cctv',
];

/**
 * AIDraftPanel — Package 5
 * ------------------------
 * OpenAI-powered quote-draft assistant. The user writes a short job
 * description; if AI needs more context it asks up to 3 short questions,
 * then hands back a fully editable draft that can be applied to the
 * QuoteForm state.
 *
 * Contract:
 *  - Prices are NEVER produced by AI — they come from the tenant's catalog.
 *  - Free plan → single package; Pro plan → single or 3-tier (Economy /
 *    Recommended / Premium).
 *  - The draft is NOT auto-saved — the user must review and click Save.
 *  - The AI quota is only decremented on a successful draft.
 */
export const AIDraftPanel = ({
  disabled,
  isPro,
  industryKey,
  industryName,
  onApplyDraft,   // ({ title, notes, package_mode, packages, currency }) => void
}) => {
  const { t, i18n } = useTranslation('quotes');
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [packageMode, setPackageMode] = useState('single');
  const [loading, setLoading] = useState(false);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [questions, setQuestions] = useState(null); // string[]
  const [answers, setAnswers] = useState({});        // { [idx]: string }
  const [missingPrices, setMissingPrices] = useState(null); // {catalog_item_id, name}[]
  const [meta, setMeta] = useState(null);

  // Sync lock — React state updates are async, so the disabled button
  // can still fire duplicate POSTs on fast repeat clicks. A ref blocks
  // in-flight submissions synchronously.
  const inFlightRef = useRef(false);

  // Only this endpoint waits 120s; global axios timeout is unchanged.
  const AI_DRAFT_TIMEOUT_MS = 120_000;

  const getPlaceholder = () => {
    if (industryKey && INDUSTRY_KEYS.includes(industryKey)) {
      return t(`ai.placeholders.${industryKey}`);
    }
    return t('ai.placeholders.neutral');
  };

  const reset = () => {
    setQuestions(null);
    setAnswers({});
    setMissingPrices(null);
    setMeta(null);
    setQuotaBlocked(false);
  };

  const callDraft = async (clarifications = null) => {
    if (!description.trim() || description.trim().length < 5) {
      toast.error(t('ai.descTooShort'));
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setMissingPrices(null);
    try {
      const uiLanguage = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
      const payload = {
        description: description.trim(),
        package_mode: isPro ? packageMode : 'single',
        clarifications: clarifications || undefined,
        language: uiLanguage === 'en' ? 'en' : 'tr',
      };
      const { data } = await axios.post(`${API_URL}/api/quote/ai/draft`, payload, {
        timeout: AI_DRAFT_TIMEOUT_MS,
      });
      setMeta(data.meta || null);

      if (data.status === 'need_clarification') {
        setQuestions(data.questions || []);
        setAnswers({});
        toast(t('ai.clarifyToast'));
        return;
      }

      // status === 'ready'
      if (data.draft) {
        onApplyDraft?.(data.draft);
        toast.success(t('ai.readyToast'));
        setQuestions(null);
        setAnswers({});
        setOpen(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const isAxiosTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');

      if (status === 504 || (typeof detail === 'object' && detail?.reason === 'ai_timeout')) {
        toast.error(
          (typeof detail === 'object' && detail?.message) || t('ai.timeout')
        );
        return;
      }
      if (isAxiosTimeout) {
        toast.error(t('ai.slow'));
        return;
      }
      if (status === 402 && typeof detail === 'object' && detail?.reason === 'ai_quota_exhausted') {
        setQuotaBlocked(true);
        return;
      }
      if (status === 402 && typeof detail === 'object' && detail?.reason === 'multi_package_locked') {
        toast.error(detail.message || t('ai.multiPackageLocked'), {
          action: {
            label: t('form.upgradeCta'),
            onClick: () => { window.location.href = '/quote-upgrade'; },
          },
        });
        return;
      }
      if (status === 422 && typeof detail === 'object' && detail?.reason === 'missing_prices') {
        setMissingPrices(detail.missing_prices || []);
        toast.error(t('ai.missingPricesToast'));
        return;
      }
      if (status === 422 && typeof detail === 'object' && detail?.reason === 'catalog_empty') {
        toast.error(detail.message || t('ai.catalogEmpty'));
        return;
      }
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || t('ai.genericError');
      toast.error(msg);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  const submitAnswers = () => {
    if (!questions?.length) return;
    const filled = {};
    questions.forEach((q, i) => {
      const v = (answers[i] || '').trim();
      if (v) filled[q] = v;
    });
    if (Object.keys(filled).length === 0) {
      toast.error(t('ai.clarifyAtLeastOne'));
      return;
    }
    callDraft(filled);
  };

  return (
    <div
      className="bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 border border-indigo-200 rounded-2xl p-4 mb-4"
      data-testid="ai-draft-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="ai-draft-toggle"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1A1A2E]">{t('ai.panelTitle')}</p>
            <p className="text-[11px] text-[#64748B]">
              {t('ai.panelSubtitle')}
            </p>
          </div>
        </div>
        <span className="text-xs text-indigo-600 font-medium">{open ? t('common.hide') : t('common.open')}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {quotaBlocked ? (
            <div
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
              data-testid="ai-draft-quota-exhausted"
            >
              <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">{t('ai.quotaTitle')}</p>
                <p className="text-xs text-amber-800 mt-1">
                  {t('ai.quotaMessage')}
                </p>
                <Link
                  to="/quote-upgrade"
                  className="mt-2 inline-flex text-sm font-semibold text-indigo-600 hover:underline"
                  data-testid="ai-draft-upgrade-link"
                >
                  {t('ai.quotaCta')}
                </Link>
              </div>
            </div>
          ) : missingPrices ? (
            <div
              className="bg-rose-50 border border-rose-200 rounded-xl p-4"
              data-testid="ai-draft-missing-prices"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-rose-900">
                    {t('ai.missingPricesTitle')}
                  </p>
                  <ul className="mt-2 text-xs text-rose-800 space-y-1">
                    {missingPrices.map((m) => (
                      <li key={m.catalog_item_id}>• {m.name || m.catalog_item_id}</li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to="/quote-catalog"
                      className="inline-flex items-center text-xs font-semibold text-rose-700 hover:underline"
                      data-testid="ai-draft-goto-catalog"
                    >
                      {t('ai.missingPricesGoto')}
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={reset}
                      data-testid="ai-draft-retry"
                    >
                      {t('ai.missingPricesRetry')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : questions ? (
            <div className="space-y-3" data-testid="ai-draft-clarify">
              <p className="text-sm font-medium text-[#1A1A2E]">
                {t('ai.clarifyIntro')}
              </p>
              <div className="space-y-2">
                {questions.map((q, i) => (
                  <div key={i}>
                    <Label className="text-xs">{q}</Label>
                    <Input
                      value={answers[i] || ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                      placeholder={t('ai.clarifyAnswerPlaceholder')}
                      data-testid={`ai-draft-answer-${i}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={submitAnswers}
                  disabled={loading}
                  className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 hover:from-indigo-600 hover:to-fuchsia-700 text-white gap-2"
                  data-testid="ai-draft-submit-answers"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {loading ? t('ai.submitLoading') : t('ai.clarifySubmit')}
                </Button>
                <Button
                  variant="outline"
                  onClick={reset}
                  disabled={loading}
                  data-testid="ai-draft-cancel-clarify"
                >
                  {t('ai.clarifyCancel')}
                </Button>
              </div>
              {loading && (
                <p
                  className="text-[11px] text-indigo-700 flex items-center gap-1"
                  data-testid="ai-draft-wait-hint"
                >
                  <Clock className="w-3 h-3" />
                  {t('ai.waitHint')}
                </p>
              )}
            </div>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Label className="text-xs">{t('ai.descLabel')}</Label>
                  {industryName && (
                    <span
                      className="text-[10px] text-indigo-700 bg-indigo-100 rounded-full px-2 py-0.5"
                      data-testid="ai-draft-industry-chip"
                    >
                      {t('ai.industryChip', { name: industryName })}
                    </span>
                  )}
                </div>
                <Textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={getPlaceholder()}
                  data-testid="ai-draft-description"
                />
                <AIMediaCapture
                  disabled={disabled || loading}
                  language={(i18n.language || 'tr').slice(0, 2)}
                  onDescription={(text) => setDescription((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text).slice(0, 2000))}
                />
              </div>

              {isPro && (
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" /> {t('ai.modeLabel')}
                  </Label>
                  <Select value={packageMode} onValueChange={setPackageMode}>
                    <SelectTrigger data-testid="ai-draft-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t('ai.modeSingle')}</SelectItem>
                      <SelectItem value="tiered">{t('ai.modeTiered')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!isPro && (
                <p className="text-[11px] text-[#94A3B8]">
                  {t('ai.freeNote')}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => callDraft()}
                  disabled={loading || disabled}
                  className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 hover:from-indigo-600 hover:to-fuchsia-700 text-white gap-2"
                  data-testid="ai-draft-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {loading ? t('ai.submitLoading') : t('ai.submit')}
                </Button>
                {meta?.model && (
                  <span className="text-[10px] text-[#94A3B8]">
                    {meta.model} · {meta.total_tokens || 0} token
                  </span>
                )}
              </div>
              {loading ? (
                <p
                  className="text-[11px] text-indigo-700 flex items-center gap-1"
                  data-testid="ai-draft-wait-hint"
                >
                  <Clock className="w-3 h-3" />
                  {t('ai.waitHint')}
                </p>
              ) : (
                <p className="text-[11px] text-[#94A3B8]">
                  {t('ai.priceNote')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIDraftPanel;
